/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import AdminTable from 'common/AdminTable';
import { toast } from 'react-toastify';
import { FaEye, FaEdit, FaTrash } from 'react-icons/fa';

const ListStartups = () => {
    const navigate = useNavigate();
    const [startups, setStartups] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchStartups = async () => {
        try {
            setLoading(true);
            const res = await getRequest('startups?all=true');
            setStartups(res?.data || []);
        } catch (err) {
            console.error('Failed to fetch startups:', err);
            toast.error("Failed to load startups list.");
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = async (id, currentStatus) => {
        try {
            await patchRequest(`startups/admin/${id}`, { is_active: !currentStatus });
            toast.success("Startup status updated successfully!");
            fetchStartups(); // Refresh list
        } catch (err) {
            console.error(err);
            toast.error("Failed to update status.");
        }
    };


    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this startup and its user account?")) {
            try {
                await deleteRequest(`startups/admin/${id}`);
                toast.success("Startup deleted successfully!");
                fetchStartups();
            } catch (err) {
                console.error(err);
                toast.error("Failed to delete startup.");
            }
        }
    };

    const tableHeaders = [
        {
            Header: 'ID',
            accessor: 'id',
        },
        {
            Header: 'Logo',
            accessor: 'logo',
            Cell: ({ row }) => (
                row.original.logo ? (
                    <img
                        src={`${process.env.REACT_APP_BACKEND}uploads/${row.original.logo}`}
                        alt="logo"
                        width={50}
                        height={50}
                        style={{ objectFit: 'cover', borderRadius: '50%' }}
                    />
                ) : (
                    <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        backgroundColor: '#ccc',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '12px'
                    }}>
                        N/A
                    </div>
                )
            ),
        },
        {
            Header: 'Startup Name',
            accessor: 'startupName',
            Cell: ({ row }) => (
                <div className="text-left font-weight-bold">
                    {row.original.startupName}
                </div>
            )
        },
        {
            Header: 'Founder Details',
            accessor: 'founder',
            Cell: ({ row }) => {
                const user = row.original.user || {};
                return (
                    <div className="text-left" style={{ fontSize: '13px' }}>
                        <div><strong>Founder:</strong> {`${user.firstName || ''} ${user.lastName || ''}`.trim()}</div>
                        <div><strong>Mobile:</strong> {user.mobile || 'N/A'}</div>
                        <div><strong>Email:</strong> {user.email || 'N/A'}</div>
                        <div><strong>Address:</strong> {user.address || 'N/A'}</div>
                    </div>
                );
            }
        },
        {
            Header: 'Program',
            accessor: 'program',
            Cell: ({ row }) => (
                <span>{row.original.program || 'N/A'}</span>
            )
        },
        {
            Header: 'Cohort Year',
            accessor: 'year',
            Cell: ({ row }) => (
                <span>{row.original.year || 'N/A'}</span>
            )
        },
        {
            Header: 'State',
            accessor: 'state',
            Cell: ({ row }) => (
                <span>{row.original.state || 'N/A'}</span>
            )
        },
        {
            Header: 'Website',
            accessor: 'website',
            Cell: ({ row }) => (
                row.original.website ? (
                    <a href={row.original.website.startsWith('http') ? row.original.website : `https://${row.original.website}`} target="_blank" rel="noopener noreferrer">
                        Visit Site
                    </a>
                ) : <span>N/A</span>
            )
        },
        {
            Header: 'Status',
            accessor: 'is_active',
            Cell: ({ row }) => (
                <button
                    className={`btn btn-sm ${row.original.is_active ? 'btn-success' : 'btn-secondary'}`}
                    onClick={() => handleStatusToggle(row.original.id, row.original.is_active)}
                    style={{ minWidth: '80px' }}
                >
                    {row.original.is_active ? 'Active' : 'Inactive'}
                </button>
            ),
        },
        {
            Header: 'Action',
            accessor: 'action',
            Cell: ({ row }) => (
                <div className="d-flex justify-content-center">
                    <button className="btn btn-sm btn-info mx-1" title="View" onClick={() => navigate(`/startups/view/${row.original.id}`)}>
                        <FaEye />
                    </button>
                    <button className="btn btn-sm btn-primary mx-1" title="Edit" onClick={() => navigate(`/startups/edit/${row.original.id}`)}>
                        <FaEdit />
                    </button>
                    <button className="btn btn-sm btn-danger mx-1" title="Delete" onClick={() => handleDelete(row.original.id)}>
                        <FaTrash />
                    </button>
                </div>
            )
        }
    ];

    useEffect(() => {
        fetchStartups();
    }, []);

    return (
        <>
            <div className="container-fluid mt-4">
                {/* Premium Animated Header Banner */}
                <div 
                    className="dashboard-header-card d-flex align-items-center justify-content-between mb-4 mx-2 shadow-lg" 
                    style={{ 
                        background: 'linear-gradient(135deg, #0d47a1 0%, #00695c 100%)', 
                        padding: '1.5rem 2rem', 
                        borderRadius: '15px',
                        animation: 'fadeInUp 0.6s ease-out forwards'
                    }}
                >
                    <div className="d-flex align-items-center">
                        <div className="icon-circle mr-3" style={{ background: 'rgba(255,255,255,0.2)', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="fa fa-rocket text-white" style={{ fontSize: '24px' }}></i>
                        </div>
                        <div>
                            <h3 className="text-white font-weight-bold mb-0" style={{ letterSpacing: '0.5px' }}>Startups Directory</h3>
                            <p className="text-white-50 mb-0 small">Manage your incubated startups</p>
                        </div>
                    </div>
                    <button 
                        className="btn btn-light font-weight-bold shadow-sm" 
                        onClick={() => navigate('/startups/add')}
                        style={{ color: '#0d47a1', borderRadius: '8px', padding: '10px 20px', transition: 'all 0.3s' }}
                        onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 5px 15px rgba(255,255,255,0.3)'; }} onFocus={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 5px 15px rgba(255,255,255,0.3)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }} onBlur={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                        <i className="fa fa-plus mr-2"></i>
                        Add New Startup
                    </button>
                </div>
                {loading ? (
                    <div className="text-center my-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                ) : startups.length > 0 ? (
                    <div className="table-responsive">
                        <AdminTable tableHeaders={tableHeaders} tableData={startups} />
                    </div>
                ) : (
                    <div className="text-center my-5">
                        <p>No startups registered yet.</p>
                    </div>
                )}
            </div>
        </>
    );
};

export default ListStartups;
